import { Wrench, AlertOctagon } from "lucide-react";
import { renderMdInline } from "./markdown";

/**
 * "Top 3 things to fix right now" — the most prescriptive surface in the
 * report. Each item quotes the actual number that's broken on this business
 * and pairs it with a single concrete fix + the impact math. Rendered between
 * Survival Timeline and Improvement Ideas as a high-contrast, narrow callout
 * so it feels like an executive briefing rather than a long list.
 */
export default function TopFixesCallout({ fixes }) {
  if (!fixes || !Array.isArray(fixes) || fixes.length === 0) return null;

  return (
    <div className="mb-6" data-testid="top-fixes-callout">
      <div className="bg-white border border-[#C26D5C]/30 rounded-2xl overflow-hidden">
        <div className="bg-[#C26D5C]/8 border-b border-[#C26D5C]/20 px-6 sm:px-8 py-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#C26D5C] flex items-center justify-center shrink-0">
            <Wrench size={16} strokeWidth={2} className="text-[#F7F5F0]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#C26D5C] font-medium">
              Top {fixes.length} {fixes.length === 1 ? "thing" : "things"} to fix right now
            </p>
            <p className="font-serif-display text-xl sm:text-2xl text-[#1F382E] mt-0.5 tracking-tight leading-tight">
              If you only do {fixes.length === 1 ? "one thing" : `${fixes.length} things`} this quarter, do {fixes.length === 1 ? "this" : "these"}.
            </p>
          </div>
        </div>

        <ol className="divide-y divide-[#E5E0D8]">
          {fixes.map((fix, i) => (
            <li
              key={i}
              className="px-6 sm:px-8 py-5 grid grid-cols-[auto_1fr] gap-x-5 gap-y-1"
              data-testid={`top-fix-${i}`}
            >
              <div className="font-serif-display text-3xl text-[#C26D5C] tabular-nums leading-none mt-1">
                {i + 1}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D] font-medium mb-1.5">
                  {fix.label}
                </p>
                <p
                  className="text-[14.5px] text-[#1A1A1A] leading-relaxed"
                  data-testid={`top-fix-${i}-problem`}
                >
                  {renderMdInline(fix.problem)}
                </p>
                <div className="mt-2.5 flex items-start gap-2">
                  <Wrench
                    size={13}
                    strokeWidth={1.75}
                    className="text-[#2C5C43] mt-1 shrink-0"
                  />
                  <p
                    className="text-[14px] text-[#1A1A1A] leading-relaxed"
                    data-testid={`top-fix-${i}-fix`}
                  >
                    <span className="font-medium text-[#2C5C43]">Fix it:</span>{" "}
                    {renderMdInline(fix.fix)}
                  </p>
                </div>
                {fix.impact && (
                  <p
                    className="mt-2 text-[12.5px] text-[#5C665D] leading-relaxed italic"
                    data-testid={`top-fix-${i}-impact`}
                  >
                    {renderMdInline(fix.impact)}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="bg-[#F7F5F0] border-t border-[#E5E0D8] px-6 sm:px-8 py-3 flex items-center gap-2">
          <AlertOctagon size={12} strokeWidth={1.75} className="text-[#5C665D]" />
          <p className="text-[11px] text-[#5C665D] leading-snug">
            Tackle these in order — debt-service and profitability fixes compound first.
          </p>
        </div>
      </div>
    </div>
  );
}
